import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Space,
  Tooltip,
  Typography,
  Input,
  Modal,
  Form,
  InputNumber,
  Card,
  message,
  Select
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  ShoppingOutlined
} from "@ant-design/icons";
import { formatPrice } from "../../utils/formatters";
import { UNIT_CODE_OPTIONS, getUnitCodeLabel } from "../../constants/invoiceConstants";
import {
  searchProducts,
  createProduct,
  updateProduct,
  deleteProduct
} from "../../services/productService";
import debounce from "lodash/debounce";
import { motion } from "framer-motion";

const { Title, Text } = Typography;
const { TextArea } = Input;

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Modal states
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchProducts = useCallback(async (search = "", page = 0, size = 10) => {
    setLoading(true);
    try {
      const response = await searchProducts(search || null, page, size);
      setProducts(response.data.content || []);
      setPagination(prev => ({
        ...prev,
        current: page + 1,
        total: response.data.totalElements || 0
      }));
    } catch (error) {
      console.error("Ürünler yüklenemedi:", error);
      message.error("Ürünler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback(
    debounce((value) => {
      fetchProducts(value, 0, pagination.pageSize);
    }, 300),
    [pagination.pageSize]
  );

  useEffect(() => {
    fetchProducts("", 0, pagination.pageSize);
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleTableChange = (pag) => {
    fetchProducts(searchText, pag.current - 1, pag.pageSize);
  };

  // Create
  const handleCreate = async (values) => {
    setFormLoading(true);
    try {
      await createProduct(values);
      message.success("Ürün başarıyla oluşturuldu");
      setIsCreateModalVisible(false);
      createForm.resetFields();
      fetchProducts(searchText, pagination.current - 1, pagination.pageSize);
    } catch (error) {
      console.error("Ürün oluşturulamadı:", error);
      message.error("Ürün oluşturulamadı");
    } finally {
      setFormLoading(false);
    }
  };

  // Update
  const handleEdit = (product) => {
    setSelectedProduct(product);
    editForm.setFieldsValue(product);
    setIsEditModalVisible(true);
  };

  const handleUpdate = async (values) => {
    setFormLoading(true);
    try {
      await updateProduct(selectedProduct.id, values);
      message.success("Ürün başarıyla güncellendi");
      setIsEditModalVisible(false);
      editForm.resetFields();
      setSelectedProduct(null);
      fetchProducts(searchText, pagination.current - 1, pagination.pageSize);
    } catch (error) {
      console.error("Ürün güncellenemedi:", error);
      message.error("Ürün güncellenemedi");
    } finally {
      setFormLoading(false);
    }
  };

  // Delete
  const handleDelete = (product) => {
    setSelectedProduct(product);
    setIsDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    setFormLoading(true);
    try {
      await deleteProduct(selectedProduct.id);
      message.success("Ürün başarıyla silindi");
      setIsDeleteModalVisible(false);
      setSelectedProduct(null);
      fetchProducts(searchText, pagination.current - 1, pagination.pageSize);
    } catch (error) {
      console.error("Ürün silinemedi:", error);
      message.error("Ürün silinemedi");
    } finally {
      setFormLoading(false);
    }
  };

  const columns = [
    {
      title: "Ürün Adı",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: "Fiyat",
      dataIndex: "price",
      key: "price",
      render: (price) => (
        <Text style={{ color: "#52c41a", fontWeight: 500 }}>
          {formatPrice(price)}
        </Text>
      )
    },
    {
      title: "Açıklama",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (text) => text || <Text type="secondary">-</Text>
    },
    {
      title: "Birim",
      dataIndex: "unitCode",
      key: "unitCode",
      render: (val) => getUnitCodeLabel(val) || <Text type="secondary">-</Text>
    },
    {
      title: "KDV %",
      dataIndex: "taxRate",
      key: "taxRate",
      render: (val) => val != null ? `%${val}` : <Text type="secondary">-</Text>
    },
    {
      title: "İşlemler",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Tooltip title="Düzenle">
              <Button
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                size="small"
              />
            </Tooltip>
          </motion.div>
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Tooltip title="Sil">
              <Button
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
                size="small"
                danger
              />
            </Tooltip>
          </motion.div>
        </Space>
      )
    }
  ];

  const ProductForm = ({ form, onFinish, initialValues }) => (
    <Form form={form} layout="vertical" onFinish={onFinish} initialValues={initialValues}>
      <Form.Item
        name="name"
        label="Ürün Adı"
        rules={[{ required: true, message: "Ürün adı zorunludur" }]}
      >
        <Input placeholder="Ürün adını giriniz" />
      </Form.Item>
      <Form.Item
        name="price"
        label="Fiyat"
        rules={[{ required: true, message: "Fiyat zorunludur" }]}
      >
        <InputNumber
          min={0.01}
          step={0.01}
          style={{ width: "100%" }}
          formatter={(value) => `₺ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          parser={(value) => value.replace(/₺\s?|(,*)/g, "")}
          placeholder="Fiyat giriniz"
        />
      </Form.Item>
      <Form.Item name="description" label="Açıklama">
        <TextArea rows={3} placeholder="Ürün açıklaması (opsiyonel)" />
      </Form.Item>
      <Form.Item name="unitCode" label="Birim">
        <Select placeholder="Birim seçiniz" allowClear options={UNIT_CODE_OPTIONS} />
      </Form.Item>
      <Form.Item name="taxRate" label="KDV Oranı (%)">
        <InputNumber
          min={0}
          max={100}
          step={1}
          style={{ width: "100%" }}
          addonAfter="%"
          placeholder="KDV oranı"
        />
      </Form.Item>
    </Form>
  );

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <ShoppingOutlined style={{ marginRight: 12 }} />
          Ürün Envanteri
        </Title>
        <Text type="secondary">
          Ürünlerinizi yönetin ve sipariş oluştururken hızlıca seçin
        </Text>
      </div>

      <Card>
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <Input
            placeholder="Ürün ara..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearch}
            style={{ width: 300 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateModalVisible(true)}
          >
            Yeni Ürün
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={products}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} ürün`,
            pageSizeOptions: ["10", "20", "50"]
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Create Modal */}
      <Modal
        title="Yeni Ürün Ekle"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        confirmLoading={formLoading}
        okText="Oluştur"
        cancelText="İptal"
      >
        <ProductForm form={createForm} onFinish={handleCreate} />
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Ürün Düzenle"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          editForm.resetFields();
          setSelectedProduct(null);
        }}
        onOk={() => editForm.submit()}
        confirmLoading={formLoading}
        okText="Güncelle"
        cancelText="İptal"
      >
        <ProductForm form={editForm} onFinish={handleUpdate} />
      </Modal>

      {/* Delete Modal */}
      <Modal
        title="Ürün Sil"
        open={isDeleteModalVisible}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setSelectedProduct(null);
        }}
        onOk={confirmDelete}
        confirmLoading={formLoading}
        okText="Sil"
        okButtonProps={{ danger: true }}
        cancelText="İptal"
      >
        <p>
          <Text strong>{selectedProduct?.name}</Text> ürününü silmek istediğinize emin misiniz?
        </p>
      </Modal>
    </div>
  );
};

export default ProductsPage;
